// Shared billing snapshot for all staff AI actions and both HTTP transports.
import { readonly, ref } from 'vue'
import type { AiQuotaSummary } from '@/services/api/ai-quota-api'

const summary = ref<AiQuotaSummary | null>(null)
const loading = ref(false)
const paidUseAccepted = ref(false)
const activeAction = ref<{ id: number; mode: 'managed' | 'byok' } | null>(null)
let actionSequence = 0
let pending: Promise<void> | null = null
let generation = 0

export const aiActionBilling = {
  activeAction: readonly(activeAction),
  summary: readonly(summary),
  loading: readonly(loading),
  paidUseAccepted: readonly(paidUseAccepted),
}

export function acceptPaidAiUse(): void {
  paidUseAccepted.value = true
}

export function resetAiActionBilling(): void {
  activeAction.value = null
  generation += 1
  summary.value = null
  paidUseAccepted.value = false
  loading.value = false
  pending = null
}

export function refreshAiActionBilling(): Promise<void> {
  if (pending) return pending
  const version = generation
  loading.value = true
  pending = (async () => {
    try {
      const { aiQuotaApi } = await import('@/services/api/ai-quota-api')
      const next = await aiQuotaApi.getSummary()
      if (version === generation) summary.value = next
    } catch {
      if (version === generation) summary.value = null
    } finally {
      if (version === generation) {
        loading.value = false
        pending = null
      }
    }
  })()
  return pending
}

// Only an explicit click authorizes paid use. Serializing starts prevents another
// action from changing the billing snapshot while a handler awaits preparatory writes.
export function beginAiAction(mode: 'managed' | 'byok'): number | null {
  if (activeAction.value || (mode === 'byok' && !paidUseAccepted.value)) return null
  const id = ++actionSequence
  activeAction.value = { id, mode }
  return id
}

export function finishAiAction(id: number | null | undefined): void {
  if (id != null && activeAction.value?.id === id) activeAction.value = null
}

export function isAiGenerationRequest(url: string, method = 'GET'): boolean {
  if (method.toUpperCase() !== 'POST') return false
  return /(?:tools\/(?:catalog-(?:seo|optimization|image)|event-(?:seo|optimization|image|localization-translate|setup)|news-post-(?:seo|generate|image)|public-content-localization-translate|webshop-legal-translate|media-asset-metadata|seating-plan-ai-import)-assist(?:\/|$)|catalog-import-mapping-assist(?:\/|$)|marketing\/(?:onsite-campaigns|campaigns)\/generate-assist(?:\/|$))/.test(url)
}

// Preserve this snapshot on retries. No action permit means no paid fallback.
export function expectedAiBillingMode(): 'managed' | 'byok' {
  return activeAction.value?.mode ?? 'managed'
}
